import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import React from 'react'
import { Dialog, Modal } from 'react-aria-components'
import { useTranslation } from 'react-i18next'

import { photoModerationQuery } from '@/api'
import { Button } from '@/components/Button'
import { Flex } from '@/components/Flex'
import { ModalContent, ModalHeader } from '@/components/Modal'
import { Spinner } from '@/components/Spinner'
import './PhotoModerationDialog.css'

type Props = {
  id: number | undefined
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  onApprove: (id: number) => void
  onReject: (id: number) => void
}

export default function PhotoModerationDialog({
  id,
  isOpen,
  onOpenChange,
  onApprove,
  onReject,
}: Props) {
  const { t } = useTranslation()

  if (!id) {
    return null
  }

  return (
    <Modal isDismissable isOpen={isOpen} onOpenChange={onOpenChange} style={{ width: 700 }}>
      <Dialog aria-label="Photo moderation">
        {({ close }) => (
          <>
            <ModalHeader onClose={close}>{t('photos.photoReport')}</ModalHeader>
            <ModerationContent id={id} onApprove={onApprove} onReject={onReject} />
          </>
        )}
      </Dialog>
    </Modal>
  )
}

type ModerationContentProps = {
  id: number
  onApprove: (id: number) => void
  onReject: (id: number) => void
}

const ModerationContent = ({ id, onApprove, onReject }: ModerationContentProps) => {
  const { data, isLoading } = useQuery(photoModerationQuery(id))
  const { t } = useTranslation()

  if (isLoading) {
    return <Spinner />
  }

  if (!data) {
    return null
  }

  return (
    <ModalContent>
      <img src={data.photoURL} alt="Photo" style={{ width: '100%', aspectRatio: '4 / 3' }} />

      <Flex justify="space-between" align="center" className="PhotoModerationDialog-recipe">
        <div className="PhotoModerationDialog-title">{data?.recipeName}</div>
        <Flex gap={4}>
          <Button
            variant="negative"
            onPress={() => {
              close()
              onReject(data.id)
            }}>
            {t('photos.reject')}
          </Button>
          <Button
            variant="default"
            onPress={() => {
              close()
              onApprove(data.id)
            }}>
            {t('photos.approve')}
          </Button>
        </Flex>
      </Flex>
      <Flex direction="column">
        <div className="PhotoModerationDialog-info">
          <div className="PhotoModerationDialog-label">{t('photos.reportedAt')}</div>
          <div className="PhotoModerationDialog-value">
            User id: {data.authorId}, {format(data.creationDate, 'dd MMM yyyy')}
          </div>
        </div>
        <div className="PhotoModerationDialog-info">
          <div className="PhotoModerationDialog-label">{t('photos.description')}</div>
          <div className="PhotoModerationDialog-value">{data.description ?? '--'}</div>
        </div>
      </Flex>
    </ModalContent>
  )
}
